import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { useTranslation } from 'react-i18next';

import { useAssistant } from '@core/assistant/providers/Provider';
import AssistantScriptDialogWrapper from '@core/assistant/Screens/DefaultScreen/AssistantScriptDialogWrapper';

import AssistantScriptsService from '@shared/services/AssistantScriptsService';
import type { PlaybackPolicy } from '@shared/interfaces/assistantScript';
import type {
  ICreateAssistantScript,
  IUpdateAssistantScript,
  ScriptStep,
} from '@shared/interfaces/assistantScripts';

import MetaChips from './MetaChips';
import ScriptTimeline from './ScriptTimeline';
import TranslationsBar from './TranslationsBar';
import { MODE_ICON, MODE_META, modeForEntity } from './kind';
import { parseScriptSteps, serializeScriptSteps } from './scriptData';
import type { AiEntity } from './types';
import { useAiInspectorStyles } from './inspectorStyles';

interface IScriptTabProps {
  entity: AiEntity;
  // Region/trigger used when creating a new script for this entity (the entity
  // has no script yet but the user pressed "Create script").
  trigger: string;
  // Full trigger set to persist on create — [regionName, ...spots] for an AI
  // trigger, [spotId] for a standalone spot. The dialog only knows the single
  // primary `trigger`, so we bundle the rest here (region-canonical).
  createTriggers?: string[];
  mapName: string | undefined;
  brandingId: string | undefined;
  onScriptChanged: () => Promise<void>;
}

const ScriptTab = ({
  entity,
  trigger,
  createTriggers,
  mapName,
  brandingId,
  onScriptChanged,
}: IScriptTabProps) => {
  const { t: translate } = useTranslation();
  const classes = useAiInspectorStyles();

  const assistant = useAssistant();
  const fetchScripts = assistant?.fetchScripts;
  const clearPrerenderedAudio = assistant?.clearPrerenderedAudio;
  const baseLanguage = assistant?.availableLanguages?.[0];
  const availableLanguages = assistant?.availableLanguages;
  const audioLanguage = assistant?.audioLanguage;

  // New scripts created from this tab inherit the trigger type implied by the
  // entity's mode: Spot → ui_click, AI trigger → area_entry. This keeps the
  // taxonomy consistent (it's triggerType-driven), so a script created for a
  // spot is a clickable button and one for a trigger auto-plays on entry.
  const createTriggerType = MODE_META[modeForEntity(entity)].createTriggerType;

  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const steps = useMemo(
    () => (entity.script ? parseScriptSteps(entity.script.scriptData) : []),
    [entity.script],
  );

  // Audio prefetch cache must be cleared after any save, otherwise players
  // continue to hear the previous version of the script until they cycle out
  // of the region. See Provider.tsx:348-355 — `assistantScriptUpdateTick`
  // bumps from the backend trigger the same thing for *other* clients.
  const invalidateCaches = useCallback(async () => {
    clearPrerenderedAudio?.();
    if (fetchScripts) {
      await fetchScripts();
    }
    await onScriptChanged();
  }, [clearPrerenderedAudio, fetchScripts, onScriptChanged]);

  const handleSaveExisting = useCallback(
    async (data: IUpdateAssistantScript | ICreateAssistantScript) => {
      if (!entity.script?.scriptId) {
        return;
      }
      await AssistantScriptsService.updateAssistantScript(
        entity.script.scriptId,
        data as IUpdateAssistantScript,
      );
      setEditOpen(false);
      await invalidateCaches();
    },
    [entity.script?.scriptId, invalidateCaches],
  );

  const handleCreateNew = useCallback(
    async (data: IUpdateAssistantScript | ICreateAssistantScript) => {
      const payload: ICreateAssistantScript = {
        ...(data as ICreateAssistantScript),
        // Persist the full trigger set so a region's script also covers the
        // spots inside it; the dialog only knows the single primary trigger.
        ...(createTriggers && createTriggers.length
          ? { scriptTrigger: createTriggers }
          : {}),
      };
      await AssistantScriptsService.createAssistantScript(payload);
      setCreateOpen(false);
      await invalidateCaches();
    },
    [invalidateCaches, createTriggers],
  );

  const handleDelete = useCallback(async () => {
    if (!entity.script?.scriptId) {
      return;
    }
    setDeleting(true);
    try {
      await AssistantScriptsService.deleteAssistantScript(
        entity.script.scriptId,
      );
      setDeleteOpen(false);
      await invalidateCaches();
    } catch (deleteError) {
      console.error(
        `Failed to delete assistant script: ${(deleteError as Error).message}`,
        { scriptId: entity.script?.scriptId, trigger },
      );
    } finally {
      setDeleting(false);
    }
  }, [entity.script?.scriptId, invalidateCaches, trigger]);

  // Persist translation-only updates inline (no dialog) so the "Translate
  // missing…" link can update the script transparently.
  const handleApplyTranslations = useCallback(
    async (nextSteps: ScriptStep[]) => {
      if (!entity.script?.scriptId) {
        return;
      }
      const payload: IUpdateAssistantScript = {
        scriptData: serializeScriptSteps(nextSteps),
      };
      await AssistantScriptsService.updateAssistantScript(
        entity.script.scriptId,
        payload,
      );
      await invalidateCaches();
    },
    [entity.script?.scriptId, invalidateCaches],
  );

  if (!entity.script) {
    const EmptyIcon = MODE_ICON[modeForEntity(entity)];
    return (
      <Box>
        <Box sx={classes.card}>
          <Box sx={classes.cardHeader}>
            <Typography component="span" sx={classes.cardHeaderTitle}>
              <SmartToyOutlinedIcon sx={{ fontSize: 13 }} />
              {translate('editorMode.aiScript', 'Script')}
            </Typography>
          </Box>
          <Box sx={classes.emptyState}>
            <Box sx={classes.emptyIcon}>
              <EmptyIcon />
            </Box>
            <Typography sx={classes.emptyTitle}>
              {translate('editorMode.aiNoScriptTitle', 'No script yet')}
            </Typography>
            <Typography sx={classes.emptyDesc}>
              {translate(
                'editorMode.aiNoScriptHint',
                'No guided script — Noa stays silent when players arrive here.',
              )}
            </Typography>
            <Box
              component="button"
              type="button"
              sx={classes.ghostButton}
              onClick={() => setCreateOpen(true)}
            >
              <AddIcon />
              {translate('editorMode.aiCreateScript', 'Create script')}
            </Box>
          </Box>
        </Box>

        {createOpen ? (
          <AssistantScriptDialogWrapper
            open
            assistantScript={null}
            trigger={trigger}
            title={entity.name}
            mapName={mapName}
            brandingId={brandingId}
            defaultTriggerType={createTriggerType}
            language={audioLanguage}
            onSave={handleCreateNew}
            onClose={() => setCreateOpen(false)}
          />
        ) : null}
      </Box>
    );
  }

  const { script } = entity;
  const isDraft = entity.scriptState === 'draft';

  // Ensure required fields for the create-from-draft path exist (in case the
  // current API requires them). All non-translation edits go through the
  // existing shared dialog, which round-trips unknown fields untouched.
  const policy: PlaybackPolicy | undefined = script.playbackPolicy as
    | PlaybackPolicy
    | undefined;

  return (
    <Box>
      <Box sx={classes.card}>
        <Box sx={classes.cardHeader}>
          <Typography component="span" sx={classes.cardHeaderTitle}>
            <SmartToyOutlinedIcon sx={{ fontSize: 13 }} />
            {translate('editorMode.aiStepTimelineHeader', 'Step timeline')}
            {isDraft ? (
              <Box component="span" sx={classes.draftChip}>
                {translate('editorMode.aiDraft', 'Draft')}
              </Box>
            ) : null}
          </Typography>
          <Box
            component="button"
            type="button"
            sx={classes.iconButton}
            onClick={() => setEditOpen(true)}
            aria-label={translate('editorMode.aiEditScript', 'Edit script')}
            title={translate('editorMode.aiEditScript', 'Edit script')}
          >
            <EditOutlinedIcon sx={{ fontSize: 15 }} />
          </Box>
          <Box
            component="button"
            type="button"
            sx={[classes.iconButton, classes.iconButtonDanger]}
            onClick={() => setDeleteOpen(true)}
            aria-label={translate('editorMode.aiDeleteScript', 'Delete script')}
            title={translate('editorMode.aiDeleteScript', 'Delete script')}
          >
            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
          </Box>
        </Box>

        <ScriptTimeline steps={steps} />
        <TranslationsBar
          steps={steps}
          baseLanguage={baseLanguage}
          availableLanguages={availableLanguages}
          onApplyTranslations={handleApplyTranslations}
        />
        <MetaChips script={{ ...script, playbackPolicy: policy }} />
        <Typography sx={[classes.footHint, { marginTop: '10px' }]}>
          {translate(
            'editorMode.aiSaveHint',
            'Audio regenerates on save — prefetch cache cleared, clients refetch.',
          )}
        </Typography>
      </Box>

      {editOpen ? (
        <AssistantScriptDialogWrapper
          open
          assistantScript={script}
          title={entity.name}
          mapName={mapName}
          brandingId={brandingId}
          language={audioLanguage}
          onSave={handleSaveExisting}
          onClose={() => setEditOpen(false)}
        />
      ) : null}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>
          {translate('editorMode.aiDeleteScript', 'Delete script')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary' }}>
            {translate(
              'editorMode.aiDeleteScriptConfirm',
              'This will remove the script but keep any attached knowledge. Continue?',
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            {translate('common.cancel', 'Cancel')}
          </Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {translate('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScriptTab;
